import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';
import SEO from '../components/SEO';
import { articles } from '../data/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <h1 className="text-2xl font-bold mb-4">
          {isAr ? 'المقال غير موجود' : 'Article Not Found'}
        </h1>
        <Link to="/blog" className="text-primary hover:underline">
          {isAr ? 'العودة إلى المدونة' : 'Back to Blog'}
        </Link>
      </div>
    );
  }

  const title = isAr ? article.titleAr : article.titleEn;
  const excerpt = isAr ? article.excerptAr : article.excerptEn;
  const content = isAr ? article.contentAr : article.contentEn;
  const tag = isAr ? article.tagAr : article.tagEn;

  const relatedArticles = articles
    .filter((a) => a.slug !== slug)
    .sort((a, b) => {
      const aMatch = a.tagEn === article.tagEn ? 0 : 1;
      const bMatch = b.tagEn === article.tagEn ? 0 : 1;
      return aMatch - bMatch;
    })
    .slice(0, 3);

  return (
    <>
      <SEO
        title={`${title} | PMP Exam Simulator`}
        description={excerpt}
        canonical={`https://pmpqandanswer.web.app/blog/${article.slug}`}
      />
      <article className="max-w-3xl mx-auto px-4 py-12">
        <button
          onClick={() => navigate('/blog')}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className={`w-4 h-4 ${isAr ? 'rotate-180' : ''}`} />
          {isAr ? 'العودة إلى المدونة' : 'Back to Blog'}
        </button>

        <span className="inline-block px-3 py-1 rounded-lg text-xs font-medium bg-primary/10 text-primary mb-4">
          {tag}
        </span>

        <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
          {title}
        </h1>

        <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
          {excerpt}
        </p>

        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8 pb-8 border-b border-border">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            {article.date}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            {isAr ? `${article.readTime} دقائق قراءة` : `${article.readTime} min read`}
          </span>
        </div>

        <div className="prose prose-lg dark:prose-invert max-w-none space-y-6">
          {content.map((section, idx) => {
            if (section.startsWith('## ')) {
              return (
                <h2 key={idx} className="text-2xl font-bold mt-8 mb-4">
                  {section.replace('## ', '')}
                </h2>
              );
            }
            if (section.startsWith('### ')) {
              return (
                <h3 key={idx} className="text-xl font-semibold mt-6 mb-3">
                  {section.replace('### ', '')}
                </h3>
              );
            }
            return (
              <p key={idx} className="text-base leading-relaxed text-foreground/90">
                {section}
              </p>
            );
          })}
        </div>

        {relatedArticles.length > 0 && (
          <div className="mt-16 pt-8 border-t border-border">
            <h3 className="text-xl font-bold mb-6">
              {isAr ? 'مقالات ذات صلة' : 'Related Articles'}
            </h3>
            <div className="grid gap-4 md:grid-cols-3">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/blog/${rel.slug}`}
                  className="p-4 border border-border rounded-xl bg-card hover:shadow-premium-lg transition-all duration-200 hover:scale-[1.02] block"
                >
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary mb-2">
                    {isAr ? rel.tagAr : rel.tagEn}
                  </span>
                  <h4 className="text-sm font-semibold leading-snug mb-2">
                    {isAr ? rel.titleAr : rel.titleEn}
                  </h4>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {isAr ? rel.excerptAr : rel.excerptEn}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </>
  );
}
